import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  SimpleGrid,
  Flex,
  Image,
  Divider,
  Badge,
  Switch,
  InputGroup,
  InputLeftAddon,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  useColorMode,
  Icon,
  Grid,
  GridItem,
  Checkbox,
  Progress,
  Center,
  Spinner,
  Stack,
  HStack,
  InputLeftElement,
  InputRightElement,
  Tooltip,
  AspectRatio,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { CheckCircleIcon, ChevronRightIcon, InfoIcon } from '@chakra-ui/icons';
import { FaServer, FaFileContract, FaGlobe, FaRegCreditCard, FaShieldAlt, FaUsers, FaRocket, FaLink, FaCoins, FaFileUpload, FaNodeJs, FaIdCard, FaCheck, FaCog, FaTools, FaCode, FaPaintBrush, FaStar } from 'react-icons/fa';
import { getColor } from '../brandConfig';

// Add a pulsing animation
const pulse = keyframes`
  0% { transform: scale(0.95); }
  70% { transform: scale(1.05); }
  100% { transform: scale(0.95); }
`;

const ClubInABox: React.FC = () => {
  const { colorMode } = useColorMode();

  const [formData, setFormData] = useState({
    clubName: '',
    domain: '',
    node: 'https://node1.block52.xyz',
    tokenAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    useIpfs: false,
    allowNftsAsAvatars: false,
    nftAddress: '',
    dexAddress: '0xE592427A0AEce92De3Edee1F18E0157C05861564',
    haveAppropriateLincesce: false,
    useOurNode: false,
    nodeUrl: '',
    nodeNftId: '',
    texasHoldem: true,
    omaha: false,
  });

  const [showPayment, setShowPayment] = useState(false);
  const [deploymentStarted, setDeploymentStarted] = useState(false);
  const [deploymentStep, setDeploymentStep] = useState(0);
  const [deploymentProgress, setDeploymentProgress] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const toast = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
    }
  };

  // Define deployment step icons - using Block52 blue (#3b82f6)
  const deploymentIcons = [
    { icon: FaTools, color: "#3b82f6", label: "Working on your club..." },
    { icon: FaServer, color: "#64ffda", label: "Deploying node..." },
    { icon: FaFileContract, color: "#3b82f6", label: "Deploying game contracts..." },
    { icon: FaPaintBrush, color: "#64ffda", label: "Building front end..." },
    { icon: FaCheck, color: "#3b82f6", label: "Done! Your Club in a Box is ready." }
  ];

  const handleBuy = () => {
    setShowPayment(true);

    // Simulate payment received after 5 seconds
    setTimeout(() => {
      setDeploymentStarted(true);

      let step = 0;
      const steps = [
        "Working on your club...",
        "Deploying node...",
        "Deploying game contracts...",
        "Building front end...",
        "Done! Your Club in a Box is ready."
      ];

      const interval = setInterval(() => {
        if (step < steps.length) {
          setDeploymentStep(step);
          setDeploymentProgress((step / (steps.length - 1)) * 100);
          step++;
        } else {
          clearInterval(interval);
        }
      }, 2000);
    }, 5000);
  };

  // Features section data - using Block52 colors
  const features = [
    {
      icon: FaServer,
      title: 'Dedicated Node Infrastructure',
      description: 'Your club connects to a dedicated validator node for maximum performance and security.',
      color: '#3b82f6'
    },
    {
      icon: FaFileContract,
      title: 'Smart Contract Based',
      description: 'All club operations are secured by blockchain smart contracts for complete transparency.',
      color: '#64ffda'
    },
    {
      icon: FaShieldAlt,
      title: 'Anti-Collusion Systems',
      description: 'Advanced AI protection against bots, collusion, and other threats to game integrity.',
      color: '#3b82f6'
    },
    {
      icon: FaRegCreditCard,
      title: 'Integrated Payment Rails',
      description: 'Instant crypto payments with no banking delays or cross-border issues.',
      color: '#64ffda'
    },
    {
      icon: FaGlobe,
      title: 'Global Player Pool Access',
      description: 'Optional connection to Block52\'s growing network of players worldwide.',
      color: '#3b82f6'
    },
    {
      icon: FaUsers,
      title: 'Agent Management',
      description: 'Transparent commission tracking with QR code tools for your team.',
      color: '#64ffda'
    }
  ];

  const renderForm = () => {
    if (showPayment) {
      const deploymentSteps = [
        "Working on your club...",
        "Deploying node...",
        "Deploying game contracts...",
        "Building front end...",
        "Done! Your Club in a Box is ready."
      ];

      return (
        <VStack spacing={8} align="center">
          <Heading as="h2" size="lg" color="gray.800" textAlign="center">
            Block52 Club Setup
          </Heading>

          {!deploymentStarted ? (
            <Box mb={6} textAlign="center">
              <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
                Payment Required: $20,000 USD
              </Text>
              <Center mb={6}>
                <Image
                  src="https://placehold.co/300x300/1a2639/white?text=QR+Code"
                  alt="Payment QR Code"
                  borderRadius="md"
                />
              </Center>
              <HStack justify="center" spacing={4}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="#3b82f6"
                  size="md"
                />
                <Text color="gray.700">Waiting for payment...</Text>
              </HStack>
            </Box>
          ) : deploymentStep < 4 ? (
            <Box w="100%">
              <Progress
                value={deploymentProgress}
                colorScheme="blue"
                size="lg"
                borderRadius="md"
                mb={6}
                bgGradient="linear(to-r, #3b82f620, #64ffda20)"
                sx={{
                  "& > div:first-of-type": {
                    bgGradient: "linear(to-r, #3b82f6, #64ffda)",
                    transition: "width 0.5s ease-in-out"
                  }
                }}
              />

              <VStack>
                {deploymentIcons.map((item, idx) => (
                  <HStack
                    key={idx}
                    spacing={4}
                    w="100%"
                    p={3}
                    borderRadius="md"
                    bg={idx === deploymentStep ? `${item.color}15` : "transparent"}
                    border={idx === deploymentStep ? "1px solid" : "none"}
                    borderColor={idx === deploymentStep ? item.color : "transparent"}
                  >
                    <Center
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={idx === deploymentStep ? `${item.color}30` : idx < deploymentStep ? `${item.color}10` : "gray.100"}
                      animation={idx === deploymentStep ? `${pulse} 2s infinite` : undefined}
                    >
                      <Icon
                        as={item.icon}
                        color={idx <= deploymentStep ? item.color : "gray.400"}
                        boxSize={5}
                      />
                    </Center>
                    <Text
                      fontWeight={idx === deploymentStep ? "bold" : "normal"}
                      color={idx <= deploymentStep ? "gray.800" : "gray.400"}
                    >
                      {item.label}
                    </Text>
                    {idx < deploymentStep && (
                      <Icon as={CheckCircleIcon} color="green.500" ml="auto" />
                    )}
                  </HStack>
                ))}
              </VStack>
            </Box>
          ) : (
            <VStack spacing={6}>
              <Box
                w="120px"
                h="120px"
                borderRadius="full"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                animation={`${pulse} 3s infinite`}
              >
                <Icon as={FaStar} boxSize={16} color="white" />
              </Box>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {deploymentSteps[4]}
              </Text>
              <Text textAlign="center" color="gray.600">
                Congratulations! Your poker club is now live and ready to use.
              </Text>
              <HStack
                p={3}
                borderRadius="md"
                bg="gray.50"
                borderWidth="1px"
                borderColor="#3b82f6"
                mb={2}
              >
                <Icon as={FaGlobe} color="#3b82f6" mr={2} />
                <Text fontWeight="bold" color="gray.800">
                  {formData.clubName.toLowerCase().replace(/\s+/g, '')}.block52.xyz
                </Text>
              </HStack>
              <Button
                mt={4}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, #64ffda, #3b82f6)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(59, 130, 246, 0.4)",
                }}
                borderRadius="50px"
                fontWeight="bold"
                size="lg"
              >
                Go To Your Club
              </Button>
            </VStack>
          )}
        </VStack>
      );
    }

    return (
      <VStack spacing={4} as="form" px={2}>
        <Heading as="h2" size="md" color="gray.800" textAlign="center" mt={0} mb={1}>
          Club in a Box
        </Heading>
        <Text color="gray.600" textAlign="center" mb={2} fontSize="sm">
          Create your custom poker club on the blockchain
        </Text>

        <FormControl id="clubName" isRequired size="sm">
          <FormLabel fontWeight="bold" fontSize="sm">Club Name</FormLabel>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaUsers} color="#3b82f6" />
            </InputLeftElement>
            <Input
              name="clubName"
              value={formData.clubName}
              onChange={handleInputChange}
              placeholder="Royal Flush Poker Club"
              borderColor="gray.300"
              _hover={{ borderColor: "#3b82f6" }}
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
              pl="2.5rem"
              bg="white"
            />
          </InputGroup>
        </FormControl>

        <FormControl id="domain" size="sm">
          <FormLabel fontWeight="bold" fontSize="sm">Domain</FormLabel>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaGlobe} color="#3b82f6" />
            </InputLeftElement>
            <Input
              name="domain"
              value={formData.domain}
              onChange={handleInputChange}
              placeholder="yourclub.com (or leave blank for subdomain)"
              borderColor="gray.300"
              _hover={{ borderColor: "#3b82f6" }}
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
              pl="2.5rem"
              bg="white"
            />
          </InputGroup>
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
          <FormControl id="dexAddress" size="sm">
            <FormLabel fontWeight="bold" fontSize="sm">DEX Address</FormLabel>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaLink} color="#3b82f6" />
              </InputLeftElement>
              <Input
                name="dexAddress"
                value={formData.dexAddress}
                onChange={handleInputChange}
                placeholder="Uniswap V3 Router"
                borderColor="gray.300"
                _hover={{ borderColor: "#3b82f6" }}
                _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                pl="2.5rem"
                bg="white"
              />
            </InputGroup>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              Defaults to Uniswap V3 Router
            </Text>
          </FormControl>

          <FormControl id="tokenAddress" size="sm">
            <FormLabel fontWeight="bold" fontSize="sm">Token Address</FormLabel>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaCoins} color="#3b82f6" />
              </InputLeftElement>
              <Input
                name="tokenAddress"
                value={formData.tokenAddress}
                onChange={handleInputChange}
                placeholder="0x1234...5678"
                borderColor="gray.300"
                _hover={{ borderColor: "#3b82f6" }}
                _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                pl="2.5rem"
                bg="white"
              />
            </InputGroup>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              USDC by default (ERC-20 format)
            </Text>
          </FormControl>
        </SimpleGrid>

        <FormControl id="csvUpload" size="sm" mb={2}>
          <FormLabel fontWeight="bold" fontSize="sm">Upload .CSV Stylesheet</FormLabel>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaFileUpload} color="#3b82f6" />
            </InputLeftElement>
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              p={0}
              h="auto"
              pl="2.5rem"
              py={1.5}
              borderColor="gray.300"
              _hover={{ borderColor: "#3b82f6" }}
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
              bg="white"
              sx={{
                "::file-selector-button": {
                  height: "auto",
                  padding: "5px 10px",
                  mr: 2,
                  border: "none",
                  background: "gray.100",
                  fontSize: "sm",
                  color: "gray.700"
                }
              }}
            />
          </InputGroup>
        </FormControl>

        <Box w="100%" p={3} borderRadius="md" bg="gray.50" borderWidth="1px" borderColor="gray.200">
          <Text fontWeight="bold" fontSize="sm" mb={2}>Options</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            <FormControl display="flex" alignItems="center" size="sm">
              <Switch
                id="useIpfs"
                name="useIpfs"
                isChecked={formData.useIpfs}
                onChange={handleInputChange}
                colorScheme="blue"
                mr={2}
                size="sm"
              />
              <FormLabel htmlFor="useIpfs" mb="0" cursor="pointer" fontSize="sm">
                Enable IPFS Storage
              </FormLabel>
            </FormControl>

            <FormControl display="flex" alignItems="center" size="sm">
              <Switch
                id="allowNftsAsAvatars"
                name="allowNftsAsAvatars"
                isChecked={formData.allowNftsAsAvatars}
                onChange={handleInputChange}
                colorScheme="blue"
                mr={2}
                size="sm"
              />
              <FormLabel htmlFor="allowNftsAsAvatars" mb="0" cursor="pointer" fontSize="sm">
                Allow NFTs as Avatars
              </FormLabel>
            </FormControl>
          </SimpleGrid>

          {formData.allowNftsAsAvatars && (
            <FormControl id="nftAddress" mt={2} size="sm">
              <FormLabel fontWeight="medium" fontSize="xs">NFT Contract Address</FormLabel>
              <Input
                name="nftAddress"
                value={formData.nftAddress}
                onChange={handleInputChange}
                placeholder="0xNFT...Contract"
                size="sm"
                borderColor="gray.300"
                _hover={{ borderColor: "#3b82f6" }}
                _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                bg="white"
              />
            </FormControl>
          )}
        </Box>

        <Box w="100%" p={3} borderRadius="md" bg="gray.50" borderWidth="1px" borderColor="gray.200">
          <Text fontWeight="bold" fontSize="sm" mb={2}>Node Configuration</Text>
          <FormControl display="flex" alignItems="center" size="sm">
            <Switch
              id="useOurNode"
              name="useOurNode"
              isChecked={formData.useOurNode}
              onChange={handleInputChange}
              colorScheme="blue"
              mr={2}
              size="sm"
            />
            <FormLabel htmlFor="useOurNode" mb="0" cursor="pointer" fontSize="sm">
              Use Block52 Validator Node
            </FormLabel>
          </FormControl>

          {!formData.useOurNode && (
            <VStack spacing={2} align="start" w="95%" pl={3} mt={2}>
              <FormControl id="nodeUrl" size="sm">
                <FormLabel fontWeight="medium" fontSize="xs">Node URL</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaNodeJs} color="#3b82f6" boxSize={3} />
                  </InputLeftElement>
                  <Input
                    name="nodeUrl"
                    value={formData.nodeUrl}
                    onChange={handleInputChange}
                    placeholder="https://your-validator-node.com"
                    borderColor="gray.300"
                    _hover={{ borderColor: "#3b82f6" }}
                    _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                    pl="2.5rem"
                    bg="white"
                    size="sm"
                  />
                </InputGroup>
              </FormControl>

              <FormControl id="nodeNftId" size="sm">
                <FormLabel fontWeight="medium" fontSize="xs">Node NFT ID</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaIdCard} color="#3b82f6" boxSize={3} />
                  </InputLeftElement>
                  <Input
                    name="nodeNftId"
                    value={formData.nodeNftId}
                    onChange={handleInputChange}
                    placeholder="NFT-123456"
                    borderColor="gray.300"
                    _hover={{ borderColor: "#3b82f6" }}
                    _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                    pl="2.5rem"
                    bg="white"
                    size="sm"
                  />
                </InputGroup>
              </FormControl>
            </VStack>
          )}
        </Box>

        <Box w="100%" p={3} borderRadius="md" bg="gray.50" borderWidth="1px" borderColor="gray.200">
          <FormControl size="sm">
            <FormLabel fontWeight="bold" fontSize="sm" mb={2}>Game Types</FormLabel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              <FormControl display="flex" alignItems="center" size="sm">
                <Switch
                  id="texasHoldem"
                  name="texasHoldem"
                  isChecked={formData.texasHoldem}
                  onChange={handleInputChange}
                  colorScheme="blue"
                  mr={2}
                  size="sm"
                />
                <FormLabel htmlFor="texasHoldem" mb="0" cursor="pointer" fontSize="sm">
                  Texas Hold'em
                </FormLabel>
              </FormControl>

              <FormControl display="flex" alignItems="center" size="sm">
                <Switch
                  id="omaha"
                  name="omaha"
                  isChecked={formData.omaha}
                  onChange={handleInputChange}
                  colorScheme="blue"
                  mr={2}
                  size="sm"
                />
                <FormLabel htmlFor="omaha" mb="0" cursor="pointer" fontSize="sm">
                  Omaha
                </FormLabel>
              </FormControl>
            </SimpleGrid>
          </FormControl>
        </Box>

        <FormControl display="flex" alignItems="center" size="sm">
          <Switch
            id="haveAppropriateLincesce"
            name="haveAppropriateLincesce"
            isChecked={formData.haveAppropriateLincesce}
            onChange={handleInputChange}
            colorScheme="blue"
            mr={2}
            size="sm"
          />
          <FormLabel htmlFor="haveAppropriateLincesce" mb="0" cursor="pointer" fontSize="sm">
            I have appropriate licenses for operating a poker club
          </FormLabel>
        </FormControl>

        <Button
          w="100%"
          mt={2}
          py={4}
          alignSelf="flex-end"
          bgGradient="linear(to-r, #3b82f6, #64ffda)"
          color="white"
          _hover={{
            bgGradient: "linear(to-r, #64ffda, #3b82f6)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 15px rgba(59, 130, 246, 0.4)",
          }}
          borderRadius="50px"
          fontWeight="bold"
          fontSize="md"
          onClick={handleBuy}
          leftIcon={<FaRocket />}
        >
          Buy Now
        </Button>
      </VStack>
    );
  };

  return (
    <>
      <NavbarWithCallToAction />

      <Box
        minH="100vh"
        bg={getColor('background.main', colorMode)}
        py={10}
      >
        {/* Hero Section */}
        <Container maxW="container.xl" mb={16}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center">
            <VStack spacing={6} align="flex-start">
              <Badge
                colorScheme="blue"
                fontSize="sm"
                px={3}
                py={1}
                borderRadius="full"
                textTransform="uppercase"
              >
                Use
              </Badge>

              <Heading
                size="2xl"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
                lineHeight="1.2"
              >
                Club in a Box
              </Heading>

              <Text fontSize="xl" color={getColor('text.secondary', colorMode)}>
                Launch your poker club on the blockchain in minutes. Get all the infrastructure,
                tools, and support you need to run a successful and profitable poker operation.
              </Text>

              <List spacing={4} mt={4}>
                {['Retain full control of your club', 'Transparent operations on blockchain', 'Automated payments and commissions', 'Advanced security and anti-collusion tools'].map((item, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={CheckCircleIcon} color="#64ffda" mt="1px" />
                    <Text color={getColor('text.secondary', colorMode)}>{item}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>

            {/* Registration Form */}
            <Box
              bg="white"
              p={6}
              borderRadius="xl"
              boxShadow="xl"
              color="black"
              border="1px solid"
              borderColor="#3b82f6"
            >
              {renderForm()}
            </Box>
          </SimpleGrid>
        </Container>

        {/* The Club Owner Journey Section */}
        <Container maxW="container.xl" mt={20}>
          <VStack spacing={12}>
            <VStack textAlign="center" maxW="800px" mx="auto">
              <Heading
                size="xl"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
                mb={3}
              >
                The Club Owner Journey
              </Heading>
              <Text fontSize="lg" color={getColor('text.secondary', colorMode)}>
                From signup to success, we've streamlined the process of launching and operating your club on the blockchain.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={8}
            >
              <GridItem>
                <VStack
                  p={6}
                  borderRadius="lg"
                  bg="rgba(26, 38, 57, 0.3)"
                  border="1px solid"
                  borderColor="#3b82f6"
                  height="100%"
                  spacing={4}
                >
                  <Flex
                    h="60px"
                    w="60px"
                    borderRadius="full"
                    bg="rgba(59, 130, 246, 0.2)"
                    justify="center"
                    align="center"
                    mb={2}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="#3b82f6">1</Text>
                  </Flex>
                  <Heading size="md" color={getColor('text.primary', colorMode)}>Plug in your club</Heading>
                  <Text color={getColor('text.secondary', colorMode)} textAlign="center">
                    Complete a simple registration process and get your club set up in minutes – whether you're migrating an existing club or starting fresh.
                  </Text>
                  <Image
                    src="https://placehold.co/400x200/3b82f6/white?text=Setup+Process"
                    alt="Setup Process"
                    borderRadius="md"
                    mt="auto"
                  />
                </VStack>
              </GridItem>

              <GridItem>
                <VStack
                  p={6}
                  borderRadius="lg"
                  bg="rgba(26, 38, 57, 0.3)"
                  border="1px solid"
                  borderColor="#64ffda"
                  height="100%"
                  spacing={4}
                >
                  <Flex
                    h="60px"
                    w="60px"
                    borderRadius="full"
                    bg="rgba(100, 255, 218, 0.2)"
                    justify="center"
                    align="center"
                    mb={2}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="#64ffda">2</Text>
                  </Flex>
                  <Heading size="md" color={getColor('text.primary', colorMode)}>Start earning</Heading>
                  <Text color={getColor('text.secondary', colorMode)} textAlign="center">
                    Begin generating revenue immediately with automated rake collection, commission distribution, and real-time financial tracking.
                  </Text>
                  <Image
                    src="https://placehold.co/400x200/64ffda/1a2639?text=Revenue+Dashboard"
                    alt="Revenue Dashboard"
                    borderRadius="md"
                    mt="auto"
                  />
                </VStack>
              </GridItem>

              <GridItem>
                <VStack
                  p={6}
                  borderRadius="lg"
                  bg="rgba(26, 38, 57, 0.3)"
                  border="1px solid"
                  borderColor="#3b82f6"
                  height="100%"
                  spacing={4}
                >
                  <Flex
                    h="60px"
                    w="60px"
                    borderRadius="full"
                    bg="rgba(59, 130, 246, 0.2)"
                    justify="center"
                    align="center"
                    mb={2}
                  >
                    <Text fontSize="2xl" fontWeight="bold" color="#3b82f6">3</Text>
                  </Flex>
                  <Heading size="md" color={getColor('text.primary', colorMode)}>Grow faster</Heading>
                  <Text color={getColor('text.secondary', colorMode)} textAlign="center">
                    Leverage our marketing tools, QR code technology, transparent agent systems, and optional access to the global player pool.
                  </Text>
                  <Image
                    src="https://placehold.co/400x200/3b82f6/white?text=Growth+Tools"
                    alt="Growth Tools"
                    borderRadius="md"
                    mt="auto"
                  />
                </VStack>
              </GridItem>
            </Grid>
          </VStack>
        </Container>

        {/* Features Section */}
        <Container maxW="container.xl" mt={20}>
          <VStack spacing={12}>
            <VStack textAlign="center" maxW="800px" mx="auto">
              <Heading
                size="xl"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
                mb={3}
              >
                Everything You Need to Succeed
              </Heading>
              <Text fontSize="lg" color={getColor('text.secondary', colorMode)}>
                Our comprehensive suite of tools and features gives club owners everything they need to run a successful operation.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  p={6}
                  borderRadius="lg"
                  bg="rgba(26, 38, 57, 0.3)"
                  border="1px solid"
                  borderColor={feature.color}
                  transition="transform 0.3s"
                  _hover={{
                    transform: "translateY(-5px)",
                    boxShadow: `0 6px 20px ${feature.color}50`,
                  }}
                >
                  <Flex mb={4} align="center">
                    <Flex
                      h="50px"
                      w="50px"
                      borderRadius="lg"
                      bg={`${feature.color}20`}
                      justify="center"
                      align="center"
                      mr={4}
                    >
                      <Icon as={feature.icon} color={feature.color} boxSize={6} />
                    </Flex>
                    <Heading size="md" color={getColor('text.primary', colorMode)}>{feature.title}</Heading>
                  </Flex>
                  <Text color={getColor('text.secondary', colorMode)}>{feature.description}</Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Additional placeholder sections */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} mt={10}>
              <Box
                p={6}
                borderRadius="lg"
                bg="rgba(26, 38, 57, 0.3)"
                border="1px solid"
                borderColor="#3b82f6"
              >
                <Heading size="md" color={getColor('text.primary', colorMode)} mb={4}>Anti-Collusion & Fairness Systems</Heading>
                <Text color={getColor('text.secondary', colorMode)} mb={6}>
                  Our advanced AI-powered security systems protect your games from bots, collusion, and other threats to game integrity.
                </Text>
                <Image
                  src="https://placehold.co/800x400/3b82f6/white?text=Anti-Collusion+Systems"
                  alt="Anti-Collusion Systems"
                  borderRadius="md"
                />
              </Box>

              <Box
                p={6}
                borderRadius="lg"
                bg="rgba(26, 38, 57, 0.3)"
                border="1px solid"
                borderColor="#64ffda"
              >
                <Heading size="md" color={getColor('text.primary', colorMode)} mb={4}>Integrated Payment Infrastructure</Heading>
                <Text color={getColor('text.secondary', colorMode)} mb={6}>
                  Seamless crypto payment rails enable instant deposits and withdrawals with no banking delays or cross-border issues.
                </Text>
                <Image
                  src="https://placehold.co/800x400/64ffda/1a2639?text=Payment+Infrastructure"
                  alt="Payment Infrastructure"
                  borderRadius="md"
                />
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
              <Box
                p={6}
                borderRadius="lg"
                bg="rgba(26, 38, 57, 0.3)"
                border="1px solid"
                borderColor="#3b82f6"
              >
                <Heading size="md" color={getColor('text.primary', colorMode)} mb={4}>Agent Management Tools</Heading>
                <Text color={getColor('text.secondary', colorMode)} mb={6}>
                  Build and manage your agent network with QR code technology, transparent commission tracking, and automated payments.
                </Text>
                <Image
                  src="https://placehold.co/800x400/3b82f6/white?text=Agent+Management"
                  alt="Agent Management"
                  borderRadius="md"
                />
              </Box>

              <Box
                p={6}
                borderRadius="lg"
                bg="rgba(26, 38, 57, 0.3)"
                border="1px solid"
                borderColor="#64ffda"
              >
                <Heading size="md" color={getColor('text.primary', colorMode)} mb={4}>Validator Node Revenue</Heading>
                <Text color={getColor('text.secondary', colorMode)} mb={6}>
                  Clubs can run validator nodes for additional revenue streams and receive passive token yield from the Block52 network.
                </Text>
                <Image
                  src="https://placehold.co/800x400/64ffda/1a2639?text=Node+Revenue"
                  alt="Node Revenue"
                  borderRadius="md"
                />
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>

        {/* See It In Action - Video Section */}
        <Container maxW="container.xl" mt={20}>
          <VStack spacing={8}>
            <VStack textAlign="center" maxW="800px" mx="auto">
              <Heading
                size="xl"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
                mb={3}
              >
                See Instant Branding In Action
              </Heading>
              <Text fontSize="lg" color={getColor('text.secondary', colorMode)}>
                Watch how quickly you can customize your club's look and feel with our powerful branding tools.
              </Text>
            </VStack>

            <Box
              w="100%"
              maxW="900px"
              mx="auto"
              p={6}
              borderRadius="xl"
              bg="rgba(26, 38, 57, 0.3)"
              border="1px solid"
              borderColor="#3b82f6"
            >
              <AspectRatio ratio={16 / 9}>
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/BA7f1ykV01E?si=G8y9MZCW_oW0GqJ3"
                  title="Block52 Instant Branding Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{
                    borderRadius: '8px',
                  }}
                />
              </AspectRatio>
            </Box>
          </VStack>
        </Container>

        {/* CTA Section */}
        <Container maxW="container.xl" mt={20}>
          <Box
            p={10}
            borderRadius="xl"
            bg="rgba(26, 38, 57, 0.5)"
            border="1px solid"
            borderColor="#3b82f6"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="-50%"
              left="-10%"
              w="300px"
              h="300px"
              bg="#3b82f620"
              borderRadius="full"
              filter="blur(60px)"
              zIndex={0}
            />
            <Box
              position="absolute"
              bottom="-30%"
              right="-5%"
              w="250px"
              h="250px"
              bg="#64ffda20"
              borderRadius="full"
              filter="blur(60px)"
              zIndex={0}
            />

            <VStack spacing={6} position="relative" zIndex={1} textAlign="center">
              <Heading
                size="2xl"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                bgClip="text"
              >
                Join the Poker Network of the Future
              </Heading>

              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} maxW="800px" mx="auto">
                Position yourself early in the growing network economy. Get all the tools, technology, and support you need to run a successful and profitable poker operation.
              </Text>

              <Button
                mt={6}
                size="lg"
                px={10}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, #64ffda, #3b82f6)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(59, 130, 246, 0.4)",
                }}
                borderRadius="50px"
                fontWeight="bold"
                onClick={() => window.location.href = '#top'}
              >
                Get Started Now
              </Button>

              <Text fontSize="sm" color={getColor('text.secondary', colorMode)} mt={4}>
                No technical knowledge required. We'll guide you through every step of the process.
              </Text>
            </VStack>
          </Box>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </>
  );
};

export default ClubInABox;
